/**
 * Single message bubble — renders markdown, tool-call cards, and progress events.
 */

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { Wrench, ChevronDown, ChevronRight, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { useState } from 'react';
import clsx from 'clsx';
import type { Message as ChatMessage, ToolCall, ProgressEvent } from '@/types/chat';
import { ReviewQueueCard } from './ReviewQueueCard';
import { FinancialReportCard } from './FinancialReportCard';
import { InferredRulesCard } from './InferredRulesCard';
import { UsageBadge } from './UsageBadge';

interface MessageProps {
  message: ChatMessage;
  /** Live progress events appended after the message was created. */
  liveProgress?: ProgressEvent[];
  /** True while the assistant is still streaming. */
  streaming?: boolean;
}

export function Message({ message, liveProgress, streaming }: MessageProps) {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';
  const isSystem = message.role === 'system';

  // Defensively strip a trailing "📎 <filename>" footer from the content
  // when the same file is already shown as an attachment chip above —
  // historic cached messages had the filename duplicated in both places.
  const displayContent = (() => {
    let c = message.content || '';
    if (message.attachments && message.attachments.length > 0) {
      for (const a of message.attachments) {
        const re = new RegExp(`\\s*📎\\s*${escapeRegExp(a.filename)}\\s*$`);
        c = c.replace(re, '').trimEnd();
      }
    }
    return c;
  })();

  if (isSystem) {
    return (
      <div className="text-xs text-center text-muted-foreground italic my-2">
        {message.content}
      </div>
    );
  }

  const progress = liveProgress ?? message.progress_events ?? [];

  return (
    <div
      className={clsx(
        'flex w-full mb-5',
        isUser ? 'justify-end' : 'justify-start',
      )}
    >
      <div
        className={clsx(
          'max-w-[82%] rounded-2xl px-5 py-4 text-sm shadow-sm',
          isUser
            ? 'bg-primary text-primary-foreground rounded-br-md'
            : 'bg-card border border-border rounded-bl-md',
        )}
      >
        {/* Attachments first */}
        {message.attachments && message.attachments.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-2">
            {message.attachments.map((a) => (
              <div
                key={a.id}
                className={clsx(
                  'inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs',
                  isUser
                    ? 'bg-primary-foreground/10'
                    : 'bg-muted',
                )}
              >
                📎 {a.filename}
              </div>
            ))}
          </div>
        )}

        {/* Tool calls (collapsed-by-default cards) */}
        {message.tool_calls && message.tool_calls.length > 0 && (
          <div className="space-y-1.5 mb-2">
            {message.tool_calls.map((tc) => (
              <ToolCallCard
                key={tc.id}
                toolCall={tc}
                progressEvents={progress.filter(
                  (p) => (p as any).tool_call_id === tc.id || !(p as any).tool_call_id,
                )}
              />
            ))}
          </div>
        )}

        {/* Live progress (no tool_calls yet — show inline) */}
        {(!message.tool_calls || message.tool_calls.length === 0) &&
          progress.length > 0 && (
            <ProgressList events={progress} live={!!streaming} />
          )}

        {/* Main content */}
        {displayContent && (
          <div className={clsx(
            'prose prose-sm max-w-none',
            isUser ? 'prose-invert' : '',
          )}>
            {isAssistant ? (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeHighlight]}
              >
                {displayContent}
              </ReactMarkdown>
            ) : (
              <div className="whitespace-pre-wrap">{displayContent}</div>
            )}
          </div>
        )}

        {streaming && !message.content && progress.length === 0 && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            <span className="text-xs">Thinking…</span>
          </div>
        )}

        {/* Usage + cost badge (assistant turns only) */}
        {isAssistant && message.usage && <UsageBadge usage={message.usage} />}
      </div>
    </div>
  );
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function ToolCallCard({
  toolCall,
  progressEvents,
}: {
  toolCall: ToolCall;
  progressEvents: ProgressEvent[];
}) {
  const [expanded, setExpanded] = useState(
    toolCall.status === 'running' || toolCall.status === 'error',
  );

  const statusIcons: Record<string, React.ReactNode> = {
    running: <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-600" />,
    success: <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />,
    error: <AlertCircle className="w-3.5 h-3.5 text-destructive" />,
    idle: <Wrench className="w-3.5 h-3.5 text-muted-foreground" />,
  };
  const statusIcon = statusIcons[toolCall.status ?? 'idle'];

  return (
    <div className="border border-border rounded-md bg-muted/40 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted/60 transition-colors"
      >
        {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        {statusIcon}
        <span className="font-mono text-xs flex-1 text-left">{toolCall.tool}</span>
        {toolCall.result_summary && (
          <span className="text-xs text-muted-foreground truncate max-w-[40%]">
            {toolCall.result_summary}
          </span>
        )}
        {toolCall.duration_ms && (
          <span className="text-[10px] text-muted-foreground">
            {(toolCall.duration_ms / 1000).toFixed(1)}s
          </span>
        )}
      </button>

      {expanded && (
        <div className="px-3 py-2 border-t border-border space-y-2">
          {toolCall.input_preview && (
            <div className="text-xs">
              <div className="text-muted-foreground mb-0.5">Input</div>
              <pre className="font-mono bg-background border border-border rounded p-2 overflow-x-auto">
                {toolCall.input_preview}
              </pre>
            </div>
          )}
          {progressEvents.length > 0 && (
            <ProgressList events={progressEvents} live={toolCall.status === 'running'} />
          )}
          {toolCall.result_summary && (
            <div className="text-xs">
              <div className="text-muted-foreground mb-0.5">Result</div>
              <div className="bg-background border border-border rounded p-2">
                {toolCall.result_summary}
              </div>
            </div>
          )}
          {/* Phase 3 rich payloads — review queue + financial report */}
          {toolCall.review_queue && toolCall.review_queue.length > 0 && (
            <ReviewQueueCard
              rows={toolCall.review_queue}
              sessionId={toolCall.id}
            />
          )}
          {toolCall.financial_report && (
            <FinancialReportCard report={toolCall.financial_report} />
          )}
          {toolCall.inferred_rules && toolCall.inferred_rules.length > 0 && (
            <InferredRulesCard
              rules={toolCall.inferred_rules}
              companyId={toolCall.company_id}
              historyVouchersScanned={toolCall.history_vouchers_scanned}
            />
          )}
        </div>
      )}
    </div>
  );
}

function ProgressList({ events, live }: { events: ProgressEvent[]; live: boolean }) {
  return (
    <div className="space-y-0.5 text-xs">
      {events.map((ev, i) => {
        const isLast = i === events.length - 1;
        const isLive = live && isLast && !(ev as any).terminal;
        return (
          <div
            key={ev.seq ?? i}
            className={clsx(
              'flex items-start gap-2 py-0.5',
              isLive && 'progress-pill-live',
            )}
          >
            <span className="text-muted-foreground font-mono text-[10px] mt-0.5 shrink-0">
              {ev.seq}
            </span>
            <span className="text-foreground/80">{ev.message}</span>
          </div>
        );
      })}
    </div>
  );
}
